'use client'
import Image from 'next/image'

import { Heading } from '@/components/primitives/Heading/Heading'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { cn, getImageUrl } from '@/lib/utils'

import { CmsContainer } from '../CmsContainer/CmsContainer'
import RichTextEditor from '../RichTextEditor/RichTextEditor'

import type { ValuePropositionType } from '@/types/contentful/valuePropositionType'

const rightBorderGradient = (isLastRow?: boolean, isFirstRow?: boolean): string => {
  if (isFirstRow) return 'to top'
  if (isLastRow) return 'to bottom'

  return 'to left'
}

const bottomBorderGradient = (isLastInRow = false, isFirstInrow = false): string => {
  if (isLastInRow) return 'to right'

  return isFirstInrow ? 'to left' : 'to top'
}

const backgroundClip = 'content-box, border-box'

export const ValueProposition = ({ title, content: description, cards, topPadding, bottomPadding }: ValuePropositionType) => {
  const isDesktop = useMediaQuery('(min-width: 768px)')

  return (
    <section data-name="value-proposition" className="bg-white">
      <CmsContainer
        topPadding={topPadding}
        bottomPadding={bottomPadding}
        className="px-4 py-8 md:px-12 md:py-16 lg:px-28 lg:py-24 xl:px-30 desktop:mx-auto desktop:max-w-content desktop:px-0"
      >
        <div className="flex flex-col items-center justify-center gap-y-6 text-primary-500">
          {title && (
            <Heading as="h1" className="text-center">
              {title}
            </Heading>
          )}
          {description && (
            <div className="max-w-[782px] text-center text-base font-normal leading-6 text-grey-700">
              <RichTextEditor content={description} />
            </div>
          )}
        </div>
        <ul className="mt-6 grid grid-cols-2 md:mt-16 md:grid-cols-3">
          {cards?.map((card, index) => {
            const isLastInRow = (index + 1) % (isDesktop ? 3 : 2) === 0
            const isLastRow = index >= cards.length - (isDesktop ? 3 : 2)
            const isFirstRow = index < (isDesktop ? 3 : 2)
            const isFirstInRow = !isLastInRow && (index === 0 || index % (isDesktop ? 3 : 2) === 0)
            const imageUrl = getImageUrl(card?.fields?.image)

            return (
              <li
                key={index}
                style={{
                  backgroundClip,
                  backgroundImage: `linear-gradient(#FFFFFF, #EFEFEF), linear-gradient(${rightBorderGradient(isLastRow, isFirstRow)}, #EFEFEF 54.5%, #FFFFFF 100%)`,
                }}
                className={cn('pr-[1px]', {
                  'pr-0': isLastInRow,
                })}
              >
                <div
                  style={{
                    backgroundClip,
                    backgroundImage: `linear-gradient(#FFFFFF, #EFEFEF), linear-gradient(${bottomBorderGradient(isLastInRow, isFirstInRow)},  #EFEFEF 24.5%, #FFFFFF 100%)`,
                  }}
                  className={cn('h-full pb-[1px]', {
                    'pb-0': isLastRow,
                  })}
                >
                  <div className="flex h-full flex-col items-center gap-4 bg-white px-4 py-6 md:py-11">
                    <div className="flex size-12 items-end justify-end">
                      {imageUrl && <Image width={48} height={48} alt={card?.fields?.image?.fields?.title ?? 'Card Image'} className="aspect-auto object-cover" src={imageUrl} />}
                    </div>
                    {!!card?.fields?.label && <p className="max-w-[200px] text-center text-base leading-6 text-grey-700">{card?.fields.label}</p>}
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      </CmsContainer>
    </section>
  )
}
